/* eslint-disable camelcase */
import { IOrder } from "@/models/checkout";
import { EPaymentMethod, ETourOption } from "@/models/common/enums";
import { ToursEntity } from "@/models/home";
import { ItalianStoriesResponseTo } from "@/models/italianStory";
import { SearchResponseTO } from "@/models/search";
import { AddToCartGAnalytics, TourGAnalytics } from "@/models/tour";
import { TSignUp } from "@/models/user";
import { randomUid } from "@/utils/randomIds";

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "GTM-NFZMKR5D";
export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;
const item_brand = "Play italy";
const affiliation = "Play Italy Ecommerce";

// Small helper to read UTM params from session storage at call-time
const getUtmFromSession = () => ({
    utmSource:
        (typeof window !== "undefined" &&
            window.sessionStorage?.getItem("utm_source")) ||
        "",
    utmMedium:
        (typeof window !== "undefined" &&
            window.sessionStorage?.getItem("utm_medium")) ||
        "",
    utmCampaign:
        (typeof window !== "undefined" &&
            window.sessionStorage?.getItem("utm_campaign")) ||
        "",
    utmTerm:
        (typeof window !== "undefined" &&
            window.sessionStorage?.getItem("utm_term")) ||
        "",
    utmContent:
        (typeof window !== "undefined" &&
            window.sessionStorage?.getItem("utm_content")) ||
        "",
});

// eslint-disable-next-line camelcase
export interface GA4Item {
    item_id?: string;
    item_name?: string;
    affiliation?: string;
    coupon?: string;
    currency?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: ETourOption;
    location_id?: string;
    price?: number;
    quantity?: number;
    totalAmount?: IOrder;
    number_people?: number;
    number_rooms?: number;
}

type TBeginCheckout = {
    currency: string;
    value: number;
    coupon: string;
    number_rooms: number;
    number_people: number;
    items: {
        price: number;
        item_id: string;
        item_name: string;
        item_brand: string;
        item_category: string;
        item_variant: ETourOption;
        totalAmount: number;
        quantity: number;
        coupon: string;
    }[];
};

type TViewItem = {
    currency: string;
    value: string | number;
    items: TourGAnalytics[] | AddToCartGAnalytics[];
};

export const completeRegistration = (user: TSignUp, countryName: string) => {
    return {
        dataLayer: {
            event: "complete_registration",
            name: user.name,
            surname: user.surname,
            email: user.email,
            country: countryName,
        },
    };
};

export const shareFacebook = (url: string) => {
    return {
        dataLayer: {
            event: "share",
            method: "Facebook",
            content_type: "article",
            item_id: url,
        },
    };
};

export const shareTwitter = (url: string) => {
    return {
        dataLayer: {
            event: "share",
            method: "Twitter",
            content_type: "article",
            item_id: url,
        },
    };
};

export const videoStart = (video: string, videoTitle: string) => {
    return {
        dataLayer: {
            event: "video_start",
            video_url: video,
            // eslint-disable-next-line camelcase
            video_title: videoTitle,
        },
    };
};
export const videoStartReview = (video: string, videoTitle: string) => {
    return {
        dataLayer: {
            event: "video_start_review",
            video_url: video,
            // eslint-disable-next-line camelcase
            video_title: videoTitle,
        },
    };
};

export const videoProgress = (
    video: string,
    videoTitle: string,
    percent: number
) => {
    return {
        dataLayer: {
            event: "video_progress",
            video_url: video,
            // eslint-disable-next-line camelcase
            video_title: videoTitle,
            video_percent: percent,
        },
    };
};
export const videoProgressReview = (
    video: string,
    videoTitle: string,
    percent: number
) => {
    return {
        dataLayer: {
            event: "video_progress50%_review",
            video_url: video,
            // eslint-disable-next-line camelcase
            video_title: videoTitle,
            video_percent: percent,
        },
    };
};

export const videoComplete = (video: string, videoTitle: string) => {
    return {
        dataLayer: {
            event: "video_complete",
            video_url: video,
            // eslint-disable-next-line camelcase
            video_title: videoTitle,
        },
    };
};

export const fileDownload = (fileName: string, pageTitle: string) => {
    return {
        dataLayer: {
            event: "file_download",
            file_name: fileName,
            page_title: pageTitle,
        },
    };
};

export const startRequestQuote = (item: GA4Item) => {
    const formattedItem = {
        item_id: item.item_id,
        item_name: item.item_name,
        item_brand: item.item_brand || item_brand,
        item_category: item.item_category,
        item_variant: item.item_variant,
        price: item.price,
        quantity: item.quantity ?? 1,
    };

    return {
        dataLayer: {
            event: "start_request_quote",
            event_id: randomUid(),
            ...getUtmFromSession(),
            ecommerce: {
                currency: item.currency || "EUR",
                value: item.price || 0,
                items: [formattedItem],
            },
        },
    };
};

export const requestQuote = (
    item: GA4Item,
    userData: object,
    formData: {
        age?: number;
        people: string;
        rooms: string;
        activities: string[];
        destinations: string[];
        currency: string;
        budget?: number;
        flight: string;
        insurance: string;
        transactionId: string;
    }
) => {
    const formattedItem = {
        item_id: item.item_id,
        item_name: item.item_name,
        item_brand: item.item_brand || item_brand,
        item_category: item.item_category,
        item_variant: item.item_variant,
        price: item.price,
        quantity: item.quantity ?? 1,
    };

    // Estrai la valuta dalla stringa (es: "Euro (EUR)" -> "EUR")
    const currencyCode = formData.currency.includes("EUR")
        ? "EUR"
        : formData.currency.includes("GBP")
        ? "GBP"
        : formData.currency.includes("USD")
        ? "USD"
        : "EUR";

    // Usa il budget per persona come valore, se disponibile, altrimenti il prezzo dell'item
    const eventValue =
        formData.budget && formData.budget > 0
            ? formData.budget
            : item.price || 0;

    // Rimuovi UTM da userData se presente
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { utm, ...userDataWithoutUtm } = userData as {
        utm?: object;
        [key: string]: unknown;
    };

    return {
        dataLayer: {
            event: "request_quote",
            event_id: randomUid(),
            ...getUtmFromSession(),
            ecommerce: {
                currency: currencyCode,
                value: eventValue,
                items: [formattedItem],
            },
            user_data: userDataWithoutUtm,
            // Parametri per le conversioni avanzate
            transactionId: formData.transactionId,
            age: formData.age,
            people: formData.people,
            rooms: formData.rooms,
            activities: formData.activities,
            destinations: formData.destinations,
            currency_selected: formData.currency,
            budget: formData.budget,
            flight: formData.flight,
            insurance: formData.insurance,
        },
    };
};

export const beginCheckout = (beginCheckoutParams: TBeginCheckout) => ({
    dataLayer: {
        event: "begin_checkout",
        event_id: randomUid(),
        ...getUtmFromSession(),
        number_rooms: beginCheckoutParams.number_rooms,
        number_people: beginCheckoutParams.number_people,
        ecommerce: {
            currency: "EUR",
            value: beginCheckoutParams.items.reduce(
                (sum, item) => sum + (item.price || 0),
                0
            ),
            items: beginCheckoutParams.items.map((item, index) => ({
                item_id: item.item_id,
                item_name: item.item_name,
                item_brand: item.item_brand,
                item_category: item.item_category,
                item_variant: item.item_variant,
                number_rooms: beginCheckoutParams.number_rooms,
                number_people: beginCheckoutParams.number_people,
                price: item.price,
                quantity: item.quantity ?? 1,
                index,
                coupon: item.coupon ?? "",
                discount: 0,
            })),
        },
    },
});

export const addToCart = (
    item: GA4Item,
    totalAmount: number,
    number_rooms: number,
    number_people: number
) => {
    return {
        dataLayer: {
            event: "add_to_cart",
            event_id: randomUid(),
            ...getUtmFromSession(),
            totalAmount,
            number_people,
            number_rooms,
            ecommerce: {
                currency: "EUR",
                value: totalAmount,
                items: [
                    {
                        ...item,
                        price: totalAmount,
                        quantity: item.quantity ?? 1,
                        index: 0,
                        coupon: "",
                        discount: 0,
                        number_people,
                        number_rooms,
                    },
                ],
            },
        },
    };
};

export const viewItem = (prop: TViewItem) => {
    return prop.items.map(item => ({
        event: "view_item",
        event_id: randomUid(),
        ...getUtmFromSession(),
        ecommerce: {
            currency: prop.currency,
            value: item.price ?? 0,
            items: [item],
        },
    }));
};

export const purchase = (
    transactionId: string,
    tourId: string,
    tourTitle: string,
    tourType: string,
    option: ETourOption,
    startingPrice: number,
    totalAmount: number,
    code?: string | undefined,
    valueDiscount?: number
) => {
    return {
        dataLayer: {
            event: "purchase",
            transaction_id: transactionId,
            ...getUtmFromSession(),
            totalAmount,
            tax: 4.0,
            coupon: code || "",
            ecommerce: {
                currency: "EUR",
                value: totalAmount,
                items: [
                    {
                        item_id: tourId,
                        item_name: tourTitle,
                        coupon: code || "",
                        discount: code ? valueDiscount : 0,
                        index: 0,
                        // eslint-disable-next-line camelcase
                        item_brand,
                        item_category: tourType,
                        item_variant: option,
                        price: startingPrice,
                        quantity: 1,
                    },
                ],
            },
        },
    };
};

export const addPaymentInfo = (
    p0: number,
    code: string | undefined,
    paymentType: EPaymentMethod,
    tourId: string,
    tourTitle: string,
    valueDiscount: number,
    tourType: string,
    option: ETourOption,
    totalAmount?: number
) => {
    return {
        dataLayer: {
            event: "add_payment_info",
            event_id: randomUid(),
            ...getUtmFromSession(),
            totalAmount,
            coupon: code || "",
            payment_type: paymentType,
            ecommerce: {
                currency: "EUR",
                value: totalAmount,
                items: [
                    {
                        item_id: tourId,
                        item_name: tourTitle,
                        coupon: code || "",
                        discount: code ? valueDiscount : 0,
                        index: 0,
                        // eslint-disable-next-line camelcase
                        item_brand,
                        item_category: tourType,
                        item_variant: option,
                        price: totalAmount,
                        quantity: 1,
                    },
                ],
            },
        },
    };
};

export const laodMore = () => {
    return {
        dataLayer: {
            event: "load_more",
        },
    };
};

export type ItemListId =
    | "homepage"
    | "tours-destinations"
    | "italian-stories"
    | "italian-story";
export type ItemListName =
    | "Pagina Home"
    | "Pagina Tours"
    | "Pagina Italian Stories"
    | "Pagina Italian Story";

export const viewItemList = (
    item_list_id: ItemListId,
    item_list_name: ItemListName,
    items: GA4Item[]
) => {
    return {
        dataLayer: {
            event: "view_item_list",
            item_list_id, // id dell'elenco
            item_list_name, // nome dell'elenco
            ...getUtmFromSession(),
            ecommerce: {
                currency: "EUR",
                items,
            },
        },
    };
};

export const createGA4ItemFromTour = (
    item_list_id: ItemListId,
    item_list_name: ItemListName,
    tour: SearchResponseTO,
    index: number
): GA4Item => {
    return {
        item_id: tour.quality.tourId,
        item_name: tour.quality.title,
        index,
        item_brand,
        item_list_id,
        item_list_name,
        price: Number(
            tour.onlyPremium
                ? tour.premium.startingPrice
                : tour.quality.startingPrice
        ),
        affiliation,
        item_category: "TOUR",
        item_variant: tour.onlyPremium ? "premium" : "quality",
    } as GA4Item;
};

export const createGA4ItemFromTourEntity = (
    item_list_id: ItemListId,
    item_list_name: ItemListName,
    tour: ToursEntity,
    index: number
): GA4Item => {
    return {
        // eslint-disable-next-line no-underscore-dangle
        item_id: tour._id,
        item_name: tour.title,
        index,
        item_brand,
        item_list_id,
        item_list_name,
        price: Number(tour.price),
        affiliation,
        item_category: "TOUR",
        item_variant: tour.onlyPremium ? "premium" : "quality",
    } as GA4Item;
};

export const createGA4ItemFromBlog = (
    item_list_id: ItemListId,
    item_list_name: ItemListName,
    blog: ItalianStoriesResponseTo,
    index: number
): GA4Item => {
    return {
        // eslint-disable-next-line no-underscore-dangle
        item_id: blog._id,
        item_name: blog.title,
        index,
        item_brand,
        item_list_id,
        item_list_name,
        affiliation,
        item_category: "BLOG",
    } as GA4Item;
};
